import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTable } from 'react-table';
import { Form } from 'react-bootstrap';
import { single_application_log } from '../api/applicationsApi';

function ApplicationLogs() {
  const [applicationLogs, setApplicationLogs] = useState<object[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(50);
  const [currentLogs, setCurrentLogs] = useState<object[]>([]);

  const location = useLocation();
  const application_id = (location.state as { application_id: number })?.application_id;

  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(['applicationLogs', application_id], () =>
    single_application_log(application_id), {
      refetchOnWindowFocus: false,
      enabled: !!application_id, // prevent API call if id is undefined
    }
  );

  useEffect(() => {
    if (data) {
      setApplicationLogs(data.data);
    }
  }, [data]);

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;

  useEffect(() => {
    if (applicationLogs.length > 0) {
      setCurrentLogs(applicationLogs.slice(indexOfFirstLog, indexOfLastLog));
    }
  }, [applicationLogs, indexOfFirstLog, indexOfLastLog]);

  const handlePageChange = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(applicationLogs.length / logsPerPage);

  const getPageNumbers = (): Array<number | string> => {
    const pageNumbers: Array<number | string> = [];
    const totalPageDisplay = 5;
    let startPage = Math.max(1, currentPage - Math.floor(totalPageDisplay / 2));
    let endPage = startPage + totalPageDisplay - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageDisplay + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    if (startPage > 1) {
      pageNumbers.unshift('...');
    }
    if (endPage < totalPages) {
      pageNumbers.push('...');
    }
    return pageNumbers;
  };

  if (isLoading) {
    return (
      <div className='text-center'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !Array.isArray(applicationLogs) || applicationLogs.length === 0) {
  return (
    <div className="text-center fw-bold fs-4 text-muted">
      No records found!
      <div className="mt-4">
        <button
          className="btn btn-sm btn-light-dark"
          onClick={() => navigate('/applications/applications-list')}
        >
          ← Back to Listing
        </button>
      </div>
    </div>
  );
}
  const columns = [
    {
      Header: "#",
      accessor: "logsDetails.id",
      width: 50,
    },
    {
      Header: "Description",
      accessor: "logsDetails.description",
      width: 300,
      Cell: ({ row }) => (
        <>
          {row.original.logsDetails?.description}
        </>
      ),
    },
    {
      Header: "Changes",
      accessor: "properties",
      Cell: ({ row }) => (
        <>
          <div className="table w-55">
            <table className="table table-rounded table-striped border gy-7 gs-7 align-middle">
              <thead className="border border-1 border-white">
                <tr>
                  <th className="ps-5 fw-bold">Column</th>
                  <th className="ps-5 fw-bold">Old</th>
                  <th className="ps-5 fw-bold">New</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(row.original.properties?.new || {}).map((key: string) => {
                  let oldValue = row.original.properties.old?.[key];
                  let newValue = row.original.properties.new?.[key];

                  if (key.toLowerCase() === "status") {
                    oldValue = oldValue === 1 ? "Active" : oldValue === 0 ? "Inactive" : oldValue;
                    newValue = newValue === 1 ? "Active" : newValue === 0 ? "Inactive" : newValue;
                  }

                  if (oldValue !== newValue || (oldValue && !newValue)) {
                    return (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{oldValue ?? ""}</td>
                        <td>{newValue ?? ""}</td>
                      </tr>
                    );
                  }

                  return null;
                })}
                {Object.keys(row.original.properties?.old || {}).map((key: string) => {
                  if (!(key in (row.original.properties.new || {}))) {
                    return (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{row.original.properties.old[key]}</td>
                        <td></td>
                      </tr>
                    );
                  }
                  return null;
                })}
              </tbody>
            </table>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Logs of Application</span>
        </h3>
      </div>
      <div className='card-body py-3'>
        <Table columns={columns} data={currentLogs} />
        <div className="text-muted mt-3">
          Showing {indexOfFirstLog + 1} to{' '}
          {indexOfLastLog > applicationLogs.length ? applicationLogs.length : indexOfLastLog}{' '}
          of {applicationLogs.length} entries
        </div>
        <div className="mb-3 d-flex justify-content-around align-items-center">
          <div className="d-flex justify-content-between align-items-center w-25">
            <label htmlFor="logsPerPage" className="form-label me-2">
              Results Per Page
            </label>
            <Form.Control
              as="select"
              id="logsPerPage"
              className="form-select form-select-sm w-50"
              value={logsPerPage}
              onChange={(e) => {
                setLogsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </Form.Control>
          </div>
          <ul className="pagination my-5">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                Previous
              </button>
            </li>
            {getPageNumbers().map((pageNumber, index) => (
              <li
                key={index}
                className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => typeof pageNumber === 'number' && handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const Table = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });
  return (
    <table
      {...getTableProps()}
      className="table table-rounded table-bordered border-gray-200 align-middle"
    >
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()} className="fs-6 text-gray-800">
            {headerGroup.headers.map(column => (
              <th {...column.getHeaderProps()} style={{ width: column.width }} className="fw-bold">
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => (
                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ApplicationLogs;
